/**
 * <s-discord-members> — Replaces placeholder avatars with the live members
 * returned by the Discord server widget (only members the widget exposes).
 *
 * Element dataset:
 *   data-guild        — Discord server ID with the widget enabled.
 *   data-online-label — Text for the online count, "%n" is replaced by the number.
 *
 * Child markup:
 *   ul[data-track]    — Marquee list; its children are replaced on success.
 *   [data-online]     — Hidden element that receives the online count.
 */
const STATUS = { online: '#22c55e', idle: '#f59e0b', dnd: '#ef4444' };

class SDiscordMembers extends HTMLElement {
  connectedCallback() {
    const guild = this.dataset.guild;
    if (!guild) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        this.#load(guild);
      }
    }, { rootMargin: '300px' });
    io.observe(this);
  }

  async #load(guild) {
    try {
      const res = await fetch(`https://discord.com/api/guilds/${guild}/widget.json`);
      if (!res.ok) return;
      const data = await res.json();
      const members = (data.members || []).slice(0, 30);
      const online = this.querySelector('[data-online]');
      if (online && typeof data.presence_count === 'number') {
        online.textContent = (this.dataset.onlineLabel || '%n').replace('%n', data.presence_count.toLocaleString());
        online.hidden = false;
      }
      if (members.length < 6) return;
      const track = this.querySelector('[data-track]');
      if (!track || !this.querySelector('template[data-item]')) return;
      const frag = document.createDocumentFragment();
      for (let pass = 0; pass < 2; pass++) {
        for (const m of members) frag.append(this.#item(m));
      }
      track.replaceChildren(frag);
    } catch (_) {
      /* keep placeholders */
    }
  }

  #item(m) {
    const tpl = this.querySelector('template[data-item]');
    const li = tpl.content.firstElementChild.cloneNode(true);
    li.querySelector('[data-avatar]').src = m.avatar_url;
    li.querySelector('[data-status]').style.background = STATUS[m.status] || STATUS.online;
    li.querySelector('[data-name]').textContent = m.username;
    return li;
  }
}

if (!customElements.get('s-discord-members')) customElements.define('s-discord-members', SDiscordMembers);
